"use client";

import * as React from "react";
import { Volume2, VolumeX, Maximize2, Minimize2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { robotSound } from "./robot-sound";
import { ASSET, PARTS, ROBOT_CSS } from "./robot-stage";
import { RobotChamber, RobotQuestionArea } from "./robot-parts";
import type { PlayQuestionDTO, PlayResult } from "@/types/quiz";

type Screen = "intro" | "guide" | "assembly" | "question" | "victory";

function fmtTime(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function shuffle(n: number): number[] {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/**
 * "Chế tạo Robot" — robot mờ sẵn 6 bộ phận. Chọn 1 bộ phận → câu hỏi: ĐÚNG thì bộ
 * phận hiện rõ (đã lắp), SAI thì vẫn mờ và chọn lại được (được cấp một câu hỏi
 * KHÁC từ kho). Lắp đủ 6 bộ phận là hoàn thành.
 *
 * Kho câu hỏi có thể NHIỀU HƠN 6: mỗi bộ phận bốc 1 câu, trả lời sai thì bộ phận
 * đó rút câu chưa dùng khác. Cùng `QuizGameProps`, sân khấu 16:9 + toàn màn hình.
 */
export function RobotBuild({
  questions,
  timeLimitSec = 0,
  onFinish,
  resultSlot,
  onReplay,
}: {
  questions: PlayQuestionDTO[];
  timeLimitSec?: number;
  onFinish: (r: PlayResult) => void;
  resultSlot?: React.ReactNode;
  onReplay?: () => void;
}) {
  const total = Math.min(PARTS.length, questions.length); // số bộ phận chơi được
  const parts = PARTS.slice(0, total);

  const [screen, setScreen] = React.useState<Screen>("intro");
  const [current, setCurrent] = React.useState<number | null>(null);
  const [completed, setCompleted] = React.useState<Record<number, boolean>>({});
  const [partQ, setPartQ] = React.useState<number[]>([]); // câu hỏi (index kho) của từng bộ phận
  const [mark, setMark] = React.useState<{ idx: number; ok: boolean } | null>(null);
  const [feedback, setFeedback] = React.useState<{ kind: "good" | "bad"; text: string } | null>(null);
  const [muted, setMuted] = React.useState(false);
  const [isFull, setIsFull] = React.useState(false);
  const [elapsed, setElapsed] = React.useState(0);
  const [finishMs, setFinishMs] = React.useState(0);

  const stageRef = React.useRef<HTMLDivElement>(null);
  const answersRef = React.useRef<{ questionId: string; chosenIndex: number }[]>([]);
  const recordedRef = React.useRef<Set<number>>(new Set()); // bộ phận đã ghi nhận lượt đầu
  const usedRef = React.useRef<Set<number>>(new Set()); // câu hỏi (index kho) đã dùng
  const startRef = React.useRef<number>(Date.now());
  const finishedRef = React.useRef(false);
  const transitionRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const doneCount = Object.keys(completed).length;

  React.useEffect(() => {
    const onFs = () => setIsFull(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  React.useEffect(() => () => { if (transitionRef.current) clearTimeout(transitionRef.current); }, []);

  const finish = React.useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    const timeMs = Date.now() - startRef.current;
    setFinishMs(timeMs);
    const byId = new Map(questions.map((qq) => [qq.id, qq.correctIndex]));
    const correct = answersRef.current.filter((a) => a.chosenIndex === byId.get(a.questionId)).length;
    robotSound.play("finish");
    onFinish({ answers: answersRef.current, timeMs, correctCount: correct });
  }, [onFinish, questions]);

  React.useEffect(() => {
    if (screen === "intro" || screen === "guide" || screen === "victory" || timeLimitSec <= 0) return;
    const id = setInterval(() => {
      const e = Date.now() - startRef.current;
      setElapsed(e);
      if (e >= timeLimitSec * 1000) {
        setScreen("victory");
        finish();
      }
    }, 250);
    return () => clearInterval(id);
  }, [screen, timeLimitSec, finish]);

  /** Bốc 1 câu hỏi chưa dùng; hết câu chưa dùng thì trả về null. */
  function nextUnused(): number | null {
    for (let k = 0; k < questions.length; k++) if (!usedRef.current.has(k)) return k;
    return null;
  }

  function resetState() {
    if (transitionRef.current) clearTimeout(transitionRef.current);
    finishedRef.current = false;
    answersRef.current = [];
    recordedRef.current = new Set();
    // Random: mỗi bộ phận 1 câu hỏi từ kho.
    const order = shuffle(questions.length);
    const assign = order.slice(0, total);
    usedRef.current = new Set(assign);
    setPartQ(assign);
    setCompleted({});
    setCurrent(null);
    setMark(null);
    setFeedback(null);
    setElapsed(0);
    startRef.current = Date.now();
  }

  function beginPlay() {
    robotSound.unlock();
    robotSound.play("click");
    resetState();
    setScreen("assembly");
  }
  function goHome() {
    robotSound.play("click");
    resetState();
    setScreen("intro");
  }
  function replay() {
    if (onReplay) {
      onReplay();
      return;
    }
    resetState();
    setScreen("assembly");
  }

  function toggleMute() {
    setMuted(robotSound.toggle());
  }
  async function toggleFullscreen() {
    const el = stageRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await el.requestFullscreen();
    } catch {
      /* bỏ qua */
    }
  }

  function openQuestion(p: number) {
    if (completed[p]) return;
    robotSound.play("click");
    setCurrent(p);
    setMark(null);
    setFeedback(null);
    setScreen("question");
  }

  function answer(ansIdx: number) {
    if (current === null || mark?.ok) return;
    const qi = partQ[current];
    if (qi === undefined) return;
    const q = questions[qi];
    if (!q) return;
    // Ghi nhận LƯỢT ĐẦU của mỗi bộ phận để chấm điểm như trắc nghiệm.
    if (!recordedRef.current.has(current)) {
      recordedRef.current.add(current);
      answersRef.current = [...answersRef.current, { questionId: q.id, chosenIndex: ansIdx }];
    }
    const ok = ansIdx === q.correctIndex;
    setMark({ idx: ansIdx, ok });
    const part = current;
    if (ok) {
      robotSound.play("correct");
      setTimeout(() => robotSound.play("assemble"), 240);
      setFeedback({ kind: "good", text: "Chính xác! Lắp ráp thành công!" });
      setCompleted((c) => ({ ...c, [part]: true }));
      const willDone = doneCount + 1;
      transitionRef.current = setTimeout(() => {
        setFeedback(null);
        setMark(null);
        setCurrent(null);
        if (willDone >= total) {
          setScreen("victory");
          finish();
        } else {
          setScreen("assembly");
        }
      }, 1050);
    } else {
      robotSound.play("wrong");
      setFeedback({ kind: "bad", text: "Chưa đúng! Bộ phận vẫn mờ, chọn lại nhé." });
      // Đổi câu hỏi khác cho bộ phận này (nếu còn câu chưa dùng), rồi về màn chọn.
      const nu = nextUnused();
      if (nu !== null) {
        usedRef.current.add(nu);
        setPartQ((arr) => arr.map((v, i) => (i === part ? nu : v)));
      }
      transitionRef.current = setTimeout(() => {
        setFeedback(null);
        setMark(null);
        setCurrent(null);
        setScreen("assembly");
      }, 1100);
    }
  }

  const qIdx = current !== null ? partQ[current] : undefined;
  const q = qIdx !== undefined ? questions[qIdx] : undefined;
  const partLabel = current !== null ? parts[current]?.label ?? "" : "";
  const timeLeft = timeLimitSec > 0 ? Math.max(0, timeLimitSec - Math.floor(elapsed / 1000)) : 0;

  return (
    <div
      ref={stageRef}
      className={cn("rbt select-none", isFull ? "grid h-screen w-screen place-items-center bg-[#176f9d]" : "")}
    >
      <style>{ROBOT_CSS}</style>

      <div
        className={cn(
          "rbt-frame relative overflow-hidden bg-[#8fd7ef] shadow-lg",
          isFull ? "aspect-[16/9] h-full max-h-full w-auto max-w-full" : "aspect-[16/9] w-full rounded-2xl"
        )}
      >
        <img src={`${ASSET}/background_phong_che_tao_robot.webp`} alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden />

        {/* Điều khiển: âm thanh · toàn màn hình (dọc, cạnh phải) */}
        <div className="absolute right-[1.5%] top-[27%] z-40 flex flex-col items-center gap-[1.6cqh]">
          <button type="button" onClick={toggleMute} aria-label={muted ? "Bật âm thanh" : "Tắt âm thanh"} className="rbt-ctrl">
            {muted ? <VolumeX className="size-full" aria-hidden /> : <Volume2 className="size-full" aria-hidden />}
          </button>
          <button type="button" onClick={toggleFullscreen} aria-label={isFull ? "Thoát toàn màn hình" : "Toàn màn hình"} className="rbt-ctrl">
            {isFull ? <Minimize2 className="size-full" aria-hidden /> : <Maximize2 className="size-full" aria-hidden />}
          </button>
        </div>

        {/* ===== INTRO ===== */}
        {screen === "intro" && (
          <div className="absolute inset-0">
            <img src={`${ASSET}/title_che_tao_robot.webp`} alt="Chế tạo Robot" className="absolute left-1/2 top-[1.5%] w-[52%] -translate-x-1/2 object-contain drop-shadow" />
            <img src={`${ASSET}/robot_trang.webp`} alt="" className="rbt-float-l absolute bottom-[1%] left-[2.5%] h-[58%] w-[25%] object-contain" aria-hidden />
            <img src={`${ASSET}/robot_vang.webp`} alt="" className="rbt-float-r absolute bottom-[1%] right-[3%] h-[58%] w-[23%] object-contain" aria-hidden />
            <button type="button" onClick={beginPlay} className="rbt-btn rbt-btn-cx absolute" style={{ top: "59%", width: "36%", height: "16%" }}>
              <img src={`${ASSET}/button_bat_dau_choi.webp`} alt="Bắt đầu chơi" />
            </button>
            <button type="button" onClick={() => { robotSound.play("click"); setScreen("guide"); }} className="rbt-btn rbt-btn-cx absolute" style={{ top: "78%", width: "29%", height: "13%" }}>
              <img src={`${ASSET}/button_huong_dan_choi.webp`} alt="Hướng dẫn chơi" />
            </button>
          </div>
        )}

        {/* ===== GUIDE ===== */}
        {screen === "guide" && (
          <div className="absolute inset-0">
            <img src={`${ASSET}/bang_huong_dan_choi.webp`} alt="Hướng dẫn chơi" className="absolute left-[12%] top-[1%] h-[91%] w-[76%] object-contain" />
            <img src={`${ASSET}/be_trai_nha_khoa_hoc.webp`} alt="" className="rbt-float-l absolute bottom-0 left-[1%] h-[58%] w-[24%] object-contain" aria-hidden />
            <img src={`${ASSET}/robot_huong_dan.webp`} alt="" className="rbt-float-r absolute bottom-[1%] right-[1%] h-[43%] w-[20%] object-contain" aria-hidden />
            <button type="button" onClick={goHome} className="rbt-btn absolute" style={{ left: "1.5%", top: "2.2%", width: "7.2%", height: "12.8%" }}>
              <img src={`${ASSET}/button_home.webp`} alt="Về trang chủ" />
            </button>
            <button type="button" onClick={beginPlay} className="rbt-btn rbt-btn-cx absolute" style={{ bottom: "2%", width: "31%", height: "13%" }}>
              <img src={`${ASSET}/button_bat_dau_choi.webp`} alt="Bắt đầu chơi" />
            </button>
          </div>
        )}

        {/* ===== ASSEMBLY (chọn bộ phận) ===== */}
        {screen === "assembly" && (
          <div className="absolute inset-0">
            <button type="button" onClick={goHome} className="rbt-btn absolute z-[12]" style={{ left: "1.5%", top: "2.2%", width: "7.2%", height: "12.8%" }}>
              <img src={`${ASSET}/button_home.webp`} alt="Về trang chủ" />
            </button>
            <img src={`${ASSET}/be_trai_nha_khoa_hoc.webp`} alt="" className="rbt-float-l absolute bottom-[1%] left-[1%] h-[57%] w-[22%] object-contain" aria-hidden />
            <div className="absolute left-1/2 top-[2%] z-10 -translate-x-1/2">
              <span className="rbt-pill">Chọn một bộ phận để trả lời câu hỏi</span>
            </div>

            {/* Buồng chế tạo — robot mờ, bộ phận đã lắp hiện rõ */}
            <RobotChamber total={total} isDone={(i) => Boolean(completed[i])} onPick={openQuestion} />

            {/* Tiến độ */}
            <div className="rbt-progress absolute z-[7]" style={{ right: "2%", bottom: "4%", width: "27%" }}>
              {timeLimitSec > 0 ? `⏱ ${timeLeft}s · ` : ""}Đã hoàn thiện {doneCount}/{total} bộ phận
              <div className="rbt-track">
                <i style={{ width: `${total ? (doneCount / total) * 100 : 0}%` }} />
              </div>
            </div>
          </div>
        )}

        {/* ===== QUESTION ===== */}
        {screen === "question" && q && (
          <div className="absolute inset-0">
            <button type="button" onClick={() => { robotSound.play("click"); if (transitionRef.current) clearTimeout(transitionRef.current); setMark(null); setFeedback(null); setCurrent(null); setScreen("assembly"); }} className="rbt-btn absolute z-[12]" style={{ left: "1.5%", top: "2.2%", width: "7.2%", height: "12.8%" }}>
              <img src={`${ASSET}/button_back.webp`} alt="Quay lại chọn bộ phận" />
            </button>
            <img src={`${ASSET}/robot_trang.webp`} alt="" className="rbt-float-l absolute bottom-[2%] left-[1.5%] h-[47%] w-[18%] object-contain" aria-hidden />

            <RobotQuestionArea
              partLabel={partLabel}
              prompt={q.prompt}
              options={q.options}
              mark={mark}
              onAnswer={answer}
              disabled={mark !== null}
            />

            {feedback && <div className={cn("rbt-feedback show", feedback.kind === "good" ? "good" : "bad")}>{feedback.text}</div>}
          </div>
        )}

        {/* ===== VICTORY ===== */}
        {screen === "victory" && (
          <div className="absolute inset-0 overflow-y-auto">
            <button type="button" onClick={goHome} className="rbt-btn absolute z-[12]" style={{ left: "1.5%", top: "2.2%", width: "7.2%", height: "12.8%" }}>
              <img src={`${ASSET}/button_home.webp`} alt="Về trang chủ" />
            </button>
            {!resultSlot && (
              <button type="button" onClick={replay} className="rbt-btn absolute z-[12]" style={{ right: "1.5%", top: "2.2%", width: "7.2%", height: "12.8%" }}>
                <img src={`${ASSET}/button_choi_lai.webp`} alt="Chơi lại" />
              </button>
            )}

            {finishMs > 0 && (
              <div className="absolute bottom-[3%] left-[3%] z-20 rounded-full bg-[#176f9de6] px-[3cqw] py-[0.9cqh] font-extrabold text-white shadow" style={{ fontSize: "clamp(11px,2cqw,24px)" }}>
                ⏱ {fmtTime(finishMs)}
              </div>
            )}

            {resultSlot ? (
              <div className="relative z-10 flex min-h-full flex-col items-center justify-center gap-[2%] px-[5%] py-[4%]">
                <img src={`${ASSET}/bang_chuc_mung.webp`} alt="Chúc mừng!" className="w-[62%] object-contain" />
                <img src={`${ASSET}/robot_chien_thang.webp`} alt="" className="h-[34%] w-[26%] object-contain" aria-hidden />
                <div className="w-[74%] max-w-full rounded-2xl bg-white/92 p-[2%] shadow-lg">{resultSlot}</div>
                <button type="button" onClick={replay} className="rbt-btn" style={{ position: "static", width: "26%", height: "10cqh" }}>
                  <img src={`${ASSET}/button_choi_lai.webp`} alt="Chơi lại" />
                </button>
              </div>
            ) : (
              <>
                <img src={`${ASSET}/bang_chuc_mung.webp`} alt="Chúc mừng!" className="absolute left-[16%] top-[1%] h-[34%] w-[68%] object-contain" />
                <img src={`${ASSET}/robot_chien_thang.webp`} alt="" className="rbt-celebrate absolute bottom-[5%] left-1/2 h-[62%] w-[29%] -translate-x-1/2 object-contain" aria-hidden />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
